import { Plus, Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { Button } from "@src/shared/components/ui/button";
import { Label } from "@src/shared/components/ui/label";
import {
  Combobox,
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  type ComboboxInputValueChangeMeta,
} from "@src/shared/components/v2/Combobox";
import { useControllableOpen } from "@src/shared/hooks/useControllableOpen";
import { cn } from "@src/shared/lib/utils";
import { captureException } from "@src/shared/utils/observability/sentry";

import type { CreatePatientFormValues } from "../../../../domain";
import { PatientEntity } from "../../../../domain";
import { CreatePatientDialogV2 } from "../CreatePatientDialog";
import { PatientSearchResults } from "./components";
import { usePatientOpener, usePatientSearchOptions } from "./hooks";
import type {
  PatientSearchOption,
  PatientSearchV2Props,
} from "./PatientSearch.types";

export type {
  PatientSearchProps,
  PatientSearchV2Props,
} from "./PatientSearch.types";

function CreatePatientSearchFooterTrigger({
  onClick,
  query,
}: {
  onClick?: () => void;
  query: string;
}) {
  const trimmedQuery = query.trim();

  return (
    <Button
      type="button"
      variant="ghost"
      className="h-12 w-full justify-start gap-3 rounded-none bg-popover px-4 text-left text-[13px] font-medium text-surface-foreground shadow-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-1"
      onClick={onClick}
    >
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Plus className="size-4 stroke-[2.3]" aria-hidden="true" />
      </span>
      <span className="min-w-0 truncate">
        Create &quot;{trimmedQuery}&quot;
      </span>
    </Button>
  );
}

export function PatientSearchV2({
  open,
  defaultOpen = false,
  disabled = false,
  autoFocus = false,
  canCreatePatient = false,
  label = "Search patients",
  placeholder = "Search a patient",
  limit = 6,
  navigateStateSource = "v2 patient search",
  className,
  inputClassName,
  contentClassName,
  listboxLabel = "Patient search results",
  emptyMessage = "No patients found.",
  selectingLabel = "opening...",
  trailingAction,
  onOpenChange,
  onSelectPatient,
}: PatientSearchV2Props) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectingKey, setSelectingKey] = useState<string | null>(null);
  const [createPatientInitialValues, setCreatePatientInitialValues] = useState<
    Partial<CreatePatientFormValues>
  >({});
  const patientOpener = usePatientOpener(navigateStateSource);
  const isSelectingPatient = selectingKey != null;
  const isDisabled = disabled || patientOpener.isOpening || isSelectingPatient;
  const { resolvedOpen, setOpen: setSearchOpen } = useControllableOpen({
    value: open,
    defaultValue: defaultOpen,
    disabled: isDisabled,
    onChange: onOpenChange,
  });
  const patientSearch = usePatientSearchOptions({
    disabled,
    limit,
    query,
  });
  const shouldShowCreateFooter =
    canCreatePatient &&
    query.trim().length > 0 &&
    !patientSearch.isSearching &&
    !patientSearch.isError &&
    patientSearch.options.length === 0;
  const searchAnchor = (
    <ComboboxAnchor asChild>
      <div className={cn("relative w-full", className)} data-combobox-anchor="">
        <Label htmlFor={inputId} className="sr-only">
          {label}
        </Label>
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <ComboboxInput
          ref={inputRef}
          asAnchor={false}
          id={inputId}
          placeholder={placeholder}
          autoComplete="off"
          className={cn(
            "h-10 rounded-[11px] border-border bg-background pr-10 pl-9 text-base shadow-[0_1px_2px_rgba(20,23,26,0.04)] focus-visible:ring-0 md-stitch:text-[13px]",
            trailingAction && "pr-12",
            inputClassName,
          )}
          disabled={isDisabled}
          onFocus={() => setSearchOpen(true)}
          onValueChange={handleInputValueChange}
          showClear
        />
        {trailingAction ? (
          <div className="absolute top-1/2 right-1.5 -translate-y-1/2">
            {trailingAction}
          </div>
        ) : null}
      </div>
    </ComboboxAnchor>
  );

  useEffect(() => {
    if (!autoFocus) return;
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [autoFocus]);

  async function handlePatientSelect(patient: PatientSearchOption["patient"]) {
    if (!onSelectPatient) {
      await patientOpener.openPatient(patient);
      return;
    }

    setSelectingKey(PatientEntity.searchResultKey(patient));
    try {
      await onSelectPatient(patient);
      setSearchOpen(false);
    } catch (err) {
      captureException(err, {
        tags: { feature: "patients-v2", action: "select-search-result" },
      });
      setSearchOpen(true);
    } finally {
      setSelectingKey(null);
    }
  }

  function handleInputValueChange(
    value: string,
    meta: ComboboxInputValueChangeMeta,
  ) {
    if (meta.source === "input" || meta.source === "clear") {
      setQuery(value);
      if (!resolvedOpen) setSearchOpen(true);
    }
  }

  return (
    <Combobox<PatientSearchOption>
      closeOnSelect={false}
      itemToStringValue={(option) => `${option.name} ${option.metadata ?? ""}`}
      itemToValue={(option) => option.key}
      items={patientSearch.options}
      label={label}
      open={resolvedOpen}
      resetInputOnClose={false}
      shouldFilter={false}
      onOpenChange={setSearchOpen}
    >
      {searchAnchor}
      <CreatePatientDialogV2
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        initialValues={createPatientInitialValues}
      />

      <ComboboxContent
        align="start"
        portalled={false}
        sideOffset={6}
        className={cn(
          "overflow-hidden rounded-[10px] p-0 shadow-[0_18px_45px_rgba(15,23,42,0.14)]",
          contentClassName,
        )}
      >
        {shouldShowCreateFooter ? (
          <CreatePatientSearchFooterTrigger
            query={query}
            onClick={() => {
              const parsedName = PatientEntity.parseName(query);
              setCreatePatientInitialValues({
                firstName: parsedName.first,
                lastName: parsedName.last,
              });
              setCreateDialogOpen(true);
              setSearchOpen(false);
            }}
          />
        ) : (
          <PatientSearchResults
            emptyMessage={emptyMessage}
            error={patientSearch.error}
            isError={patientSearch.isError}
            isOpening={patientOpener.isOpening || isSelectingPatient}
            isSearching={patientSearch.isSearching}
            isRefetching={patientSearch.isRefetching}
            listboxLabel={listboxLabel}
            onSelect={handlePatientSelect}
            openingKey={selectingKey ?? patientOpener.openingKey}
            options={patientSearch.options}
            selectingLabel={selectingLabel}
          />
        )}
      </ComboboxContent>
    </Combobox>
  );
}

export const PatientSearch = PatientSearchV2;
